import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { isEmpty } from 'ramda';
import {
  useGeneralRoutes,
  useIsComponentStillOnItsPage,
  useSportsSettings
} from '@namespace/cms';
import { useDeviceStore } from '@namespace/device';
import { useI18n } from '@namespace/i18n';
import { Icon } from '@namespace/icons2';
import { useHistory } from '@namespace/router';
import { IS_WIDGET } from '@namespace/widget';
import BackHeader from '../../common/BackHeader';
import SportbookSkelet from '../../common/SportbookSkelet';
import { LINE_KEY, MODE, PRESETS_SOURCE_TYPES } from '../../constants';
import { useGetPathParams } from '../../hooks/useGetPathParams';
import { useRemoveLine } from '../../hooks/useRemoveLine';
import {
  getLineField,
  sportsbookActions,
  useEventsIdsBySportId,
  useSportsbookStore
} from '../../store';
import { TopEventsLine } from './TopEventsLine';

import styles from './index.module.css';

const {
  FETCH_ACTIVE_TOP_EVENTS,
  SET_IS_LINE_CACHED,
  SET_IS_ALL_LINE_EVENTS_LOADED
} = sportsbookActions;

export const TopEvents = () => {
  const [isLoading, setIsLoading] = useState(true);
  const topEvents = useSportsbookStore('topEvents');
  const { t, f, language: lang } = useI18n();
  const { topId } = useGetPathParams(MODE.PRE_MATCH);
  const lineKey = `${LINE_KEY.TOP_EVENTS}_${topId}`;
  const { timeRangeForAllPeriodByEvents } = useSportsSettings();
  const { isDesktop, pageType } = useDeviceStore();
  const eventsBySportId = useEventsIdsBySportId(lineKey);
  const checkIsComponentStillOnItsPage = useIsComponentStillOnItsPage();

  const { preMatchLobby } = useGeneralRoutes();
  const { push } = useHistory();
  const configByPlatform = useMemo(
    () =>
      topEvents?.length
        ? topEvents?.find(
            ({ presetId }) => presetId.toString() === topId.toString()
          )[pageType]
        : {},
    [pageType, topEvents, topId]
  );
  const { translationKey } = configByPlatform || {};

  useEffect(() => {
    // explicitly set line loaded=true to prevent unwanted redirect later due to REMOVE_ALL_LINES
    const setIsAllEventsLoaded = () => {
      const isLineLoaded = getLineField(lineKey, 'isAllEventsLoaded');
      if (!isLineLoaded) {
        SET_IS_ALL_LINE_EVENTS_LOADED(lineKey, true);
      }

      setIsLoading(false);
    };

    if (topId && topEvents?.length) {
      if (getLineField(lineKey, 'isCached')) {
        SET_IS_LINE_CACHED(false, lineKey);
        setIsAllEventsLoaded();
        return;
      }

      if (!checkIsComponentStillOnItsPage()) {
        return;
      }

      setIsLoading(true);
      const {
        events: newEvents = [],
        tournaments: newTournaments = [],
        source = PRESETS_SOURCE_TYPES.EVENTS
      } = topEvents.find(
        ({ presetId }) => presetId.toString() === topId.toString()
      ) || {};

      FETCH_ACTIVE_TOP_EVENTS({
        source,
        events: newEvents,
        tournaments: newTournaments,
        lang,
        serviceId: MODE.PRE_MATCH,
        lineKey,
        timeRange: timeRangeForAllPeriodByEvents
      }).finally(setIsAllEventsLoaded);
    } else {
      setIsAllEventsLoaded();
    }
  }, [
    topId,
    lang,
    f,
    topEvents,
    timeRangeForAllPeriodByEvents,
    lineKey,
    checkIsComponentStillOnItsPage
  ]);

  useEffect(() => {
    if (
      !isLoading &&
      isEmpty(eventsBySportId) &&
      getLineField(lineKey, 'isAllEventsLoaded')
    ) {
      push(preMatchLobby);
    }
  }, [eventsBySportId, isLoading, lineKey, preMatchLobby, push]);

  useRemoveLine(lineKey);

  // todo refactor: remove key
  // Add key to force component unmount on topId change
  return (
    <div key={topId} className={clsx(!IS_WIDGET && styles.container)}>
      {!isDesktop && (
        <BackHeader
          title={t(translationKey)}
          arrowBackIcon={
            <Icon
              name="icons/general/nav/back"
              color="line_header_txt"
              size="s"
            />
          }
          showArrowBack={true}
        />
      )}
      {isEmpty(eventsBySportId) && isLoading ? (
        <SportbookSkelet showLineHeader={true} />
      ) : (
        Object.entries(eventsBySportId).map(
          ([sportId, eventsIdsBySport], idx) => (
            <TopEventsLine
              key={sportId}
              lineKey={lineKey}
              sportId={sportId}
              eventsIdsBySport={eventsIdsBySport}
              isLoading={isLoading}
              serviceId={MODE.PRE_MATCH}
              backLink={preMatchLobby}
              isExpandedDefault={idx < 1}
            />
          )
        )
      )}
    </div>
  );
};
